import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { User } from './types'
import { readCentralSiteIdentity, type CentralSiteIdentity } from './site-navigation'
import './site-switcher.css'

const sites = [
  { name: 'ExtraCraft', url: 'https://extracraft.net', logo: '/site-navigation/extracraft-678cfb8a8f40.webp' },
  { name: 'ExtraPanel', url: 'https://panel.extracraft.net', logo: '/site-navigation/extrapanel-6d50d1a4bd13.webp' },
  { name: 'ExtraSanctions', url: 'https://sanctions.extracraft.net', logo: '/site-navigation/extrasanctions-fa84c5764710.webp' },
  { name: 'ExtraBeta', url: 'https://beta.extracraft.net', logo: '/site-navigation/extrabeta-3d51ec1d81c1.webp' },
  { name: 'ExtraSubtitles', url: 'https://subtitles.extracraft.net', logo: '/site-navigation/extrasubtitles-15977b51e2f4.webp' },
  { name: 'ExtraNPCs Studio', url: 'https://npcs.extracraft.net', logo: '/site-navigation/extranpcs-87082d12e758.webp' },
]

export function SiteBrand({ user }: { user?: User | null }) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const trigger = useRef<HTMLButtonElement>(null)
  const menu = useRef<HTMLElement>(null)
  const pointerFocus = useRef(false)
  const restoredFocus = useRef(false)
  const [position, setPosition] = useState({ left: 8, top: 0, width: 302 })
  const sessionKey = user ? `${user.id}:${user.role}` : 'signed-out'
  const [identity, setIdentity] = useState<{ sessionKey: string | null; proof: CentralSiteIdentity | null }>({ sessionKey: null, proof: null })
  const proof = identity.sessionKey === sessionKey ? identity.proof : null
  const enabled = proof?.authenticated === true && proof.role === 'admin'
  const inside = useCallback((target: EventTarget | null) => target instanceof Node && Boolean(root.current?.contains(target) || menu.current?.contains(target)), [])
  const show = useCallback(() => {
    const bounds = trigger.current?.getBoundingClientRect()
    if (!bounds) return
    const width = Math.min(302, window.innerWidth - 16)
    setPosition({ left: Math.max(8, Math.min(bounds.left, window.innerWidth - width - 8)), top: bounds.bottom, width })
    setOpen(true)
  }, [])
  const restoreTrigger = useCallback(() => {
    restoredFocus.current = true
    trigger.current?.focus()
    restoredFocus.current = false
    setOpen(false)
  }, [])
  useEffect(() => {
    let active = true
    const controller = new AbortController()
    setOpen(false)
    void readCentralSiteIdentity(fetch, controller.signal).then(next => {
      if (active) setIdentity({ sessionKey, proof: next })
    })
    return () => { active = false; controller.abort() }
  }, [sessionKey])
  useEffect(() => {
    if (!enabled || !open) return
    const dismiss = (event: PointerEvent) => { if (!inside(event.target)) setOpen(false) }
    const escape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      if (inside(document.activeElement)) restoreTrigger()
      setOpen(false)
    }
    const scroll = (event: Event) => {
      if (!(event.target instanceof Node && menu.current?.querySelector('.ec-site-rail')?.contains(event.target))) setOpen(false)
    }
    const resize = () => setOpen(false)
    document.addEventListener('pointerdown', dismiss)
    document.addEventListener('keydown', escape)
    window.addEventListener('scroll', scroll, true)
    window.addEventListener('resize', resize)
    return () => {
      document.removeEventListener('pointerdown', dismiss)
      document.removeEventListener('keydown', escape)
      window.removeEventListener('scroll', scroll, true)
      window.removeEventListener('resize', resize)
    }
  }, [enabled, open, inside, restoreTrigger])

  const image = <img src="/brand/extranpcs.png?v=20261011" alt="" />
  const wordmark = <span><strong>ExtraNPCs</strong><small>Studio</small></span>
  if (!enabled) return <div className="site-brand-access"><div className="wordmark studio-wordmark">{image}{wordmark}</div>{user && proof?.authenticated === false && <a className="site-verification" href="https://sanctions.extracraft.net/auth/site-navigation?return=https%3A%2F%2Fnpcs.extracraft.net">Verify Minecraft staff</a>}</div>
  return (
    <div className="wordmark studio-wordmark">
      <div
        className="site-switcher"
        ref={root}
        onPointerEnter={event => { if (event.pointerType === 'mouse') show() }}
        onPointerLeave={event => { if (!inside(event.relatedTarget) && !inside(document.activeElement)) setOpen(false) }}
        onFocus={event => { if (!restoredFocus.current && !pointerFocus.current && !inside(event.relatedTarget)) show() }}
        onBlur={event => { if (!inside(event.relatedTarget)) setOpen(false) }}
        onKeyDown={event => {
          if (event.key === 'Tab' && !event.shiftKey && open) {
            event.preventDefault()
            menu.current?.querySelector<HTMLAnchorElement>('a')?.focus()
            return
          }
          if (event.key === 'ArrowDown' && event.target === trigger.current) {
            event.preventDefault()
            show()
            requestAnimationFrame(() => menu.current?.querySelector<HTMLAnchorElement>('a')?.focus())
          }
        }}
      >
        <button
          type="button"
          className="site-switcher-trigger"
          ref={trigger}
          aria-label="ExtraCraft websites"
          aria-expanded={open}
          aria-controls="extracraft-sites"
          onPointerDown={() => { pointerFocus.current = true }}
          onPointerUp={() => { pointerFocus.current = false }}
          onPointerCancel={() => { pointerFocus.current = false }}
          onClick={() => { pointerFocus.current = false; if (open) setOpen(false); else show() }}
        >{image}</button>
      </div>
      {wordmark}
      {createPortal(<nav
        className="ec-site-menu"
        ref={menu}
        id="extracraft-sites"
        hidden={!open}
        style={position}
        aria-label="ExtraCraft websites"
        onPointerEnter={event => { if (event.pointerType === 'mouse') setOpen(true) }}
        onPointerLeave={event => { if (!inside(event.relatedTarget) && !inside(document.activeElement)) setOpen(false) }}
        onBlur={event => { if (!inside(event.relatedTarget)) setOpen(false) }}
        onKeyDown={event => {
          const links = Array.from(menu.current?.querySelectorAll<HTMLAnchorElement>('a') ?? [])
          const index = links.indexOf(document.activeElement as HTMLAnchorElement)
          if (index < 0) return
          if (event.key === 'Tab') {
            if (event.shiftKey && index === 0) {
              event.preventDefault()
              restoreTrigger()
            } else if (!event.shiftKey && index === links.length - 1) {
              restoreTrigger()
            }
            return
          }
          if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
          event.preventDefault()
          const next = event.key === 'Home' ? 0 : event.key === 'End' ? links.length - 1 : (index + (event.key === 'ArrowRight' ? 1 : -1) + links.length) % links.length
          links[next].focus()
        }}
      ><div className="ec-site-rail">
        {sites.map(site => <a key={site.name} href={site.url} aria-label={site.name} title={site.name} aria-current={site.name === 'ExtraNPCs Studio' ? 'page' : undefined} onClick={() => setOpen(false)}><img src={site.logo} alt="" width="36" height="36" decoding="async" /></a>)}
      </div></nav>, document.body)}
    </div>
  )
}
